import useAxios from "./../../../Hooks/useAxios";
import { useEffect, useState } from "react";
import { FiUser, FiFileText, FiDownload } from "react-icons/fi";

function Investigations({ visits = [] }) {
  const [investigationsByVisit, setInvestigationsByVisit] = useState({});
  const request = useAxios();

  const safe = (value) => value ?? "—";

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getInvestigation = async () => {
    if (!visits?.length) return;

    const visitIds = visits.map((v) => v?.id).filter(Boolean);
    if (!visitIds.length) return;

    const res = await request({
      method: "GET",
      url: "chapchap/list/visit_investigation",
      params: { "visit_id-int": visitIds.join(",") },
      showSuccessModal: false,
      showLoadingModal: false,
    });

    if (!res?._error && res?.data?.docs?.length) {
      const grouped = res.data.docs.reduce((acc, inv) => {
        const vid = inv?.visit_id ?? "unknown";
        if (!acc[vid]) acc[vid] = [];
        acc[vid].push(inv);
        return acc;
      }, {});
      setInvestigationsByVisit(grouped);
    }
  };

  useEffect(() => {
    getInvestigation();
  }, [visits]);

  const handleDownload = async (doc) => {
    try {
      console.log(doc);

      const res = await request({
        method: "POST",
        url: "hospital/visit/documents/download",
        body: { url: doc.url },
        showSuccessModal: false,
        showLoadingModal: true,
      });

      if (!res || res === "error") return;

      // Native download
      const link = document.createElement("a");
      link.href = res.link;
      link.download = doc.name || "file";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Download failed", err);
    }
  };

  if (!Object.keys(investigationsByVisit).length) {
    return (
      <div className="p-5 text-center text-auro_metal_saurus">
        No investigation history available.
      </div>
    );
  }

  return (
    <div className="w-full p-5 flex flex-col gap-6">
      <h2 className="text-2xl font-bold text-yankees_blue mb-4">
        Patient Investigation History
      </h2>

      {Object.entries(investigationsByVisit).map(([visitId, invList]) => {
        const visit = invList[0]?.visits;
        return (
          <div
            key={visitId}
            className="bg-white shadow-xl rounded-3xl p-6 border border-bright_grey w-full"
          >
            <div className="flex items-center gap-4 border-b border-bright_grey pb-4 mb-4">
              <div className="ml-auto text-auro_metal_saurus text-sm">
                Visit Date: {formatDate(visit?.created_at)}
              </div>
            </div>

            {invList.map((inv) => (
              <div key={inv?.id} className="mb-6">
                <h3 className="text-xl font-semibold text-yankees_blue mb-2">
                  {safe(inv?.investigations?.name)}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <Detail
                    label="Requested By"
                    value={safe(
                      inv?.team_visit_investigation_requested_byToteam?.name
                    )}
                    icon={<FiUser />}
                  />
                  <Detail
                    label="Performed By"
                    value={safe(
                      inv?.team_visit_investigation_performed_byToteam?.name
                    )}
                    icon={<FiUser />}
                  />
                  <Detail
                    label="Status"
                    value={safe(inv?.investigation_status)}
                    icon={<FiFileText />}
                  />
                  <Detail
                    label="Payment Method"
                    value={safe(inv?.payment_method)}
                    icon={<FiFileText />}
                  />
                </div>

                {inv?.visit_documents?.length > 0 && (
                  <Section title="Documents">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {inv.visit_documents.map((doc) => (
                        <button
                          key={doc?.id}
                          onClick={() => handleDownload(doc)}
                          className="flex items-center justify-between p-3 bg-white rounded-xl shadow border border-bright_grey hover:shadow-md transition"
                        >
                          <span>{safe(doc?.name)}</span>
                          <FiDownload className="text-primary text-xl" />
                        </button>
                      ))}
                    </div>
                  </Section>
                )}

                {inv?.visit_comments?.length > 0 && (
                  <Section title="Comments">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {inv.visit_comments.map((c) => (
                        <div
                          key={c?.id}
                          className="p-3 bg-white rounded-xl shadow border border-bright_grey break-words"
                        >
                          <p className="font-medium text-auro_metal_saurus mb-1">
                            {safe(c?.name)}
                          </p>
                          <p className="text-yankees_blue text-sm whitespace-pre-wrap">
                            {safe(c?.description)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="mt-4 p-4 bg-ghost_white rounded-2xl shadow-sm border border-bright_grey">
    <h3 className="text-lg font-semibold text-yankees_blue mb-3">{title}</h3>
    <div className="space-y-2">{children}</div>
  </div>
);

const Detail = ({ label, value, icon }) => (
  <div className="flex justify-between items-start py-2 px-3 bg-white rounded-xl shadow border border-bright_grey">
    <div className="flex items-center gap-2 text-auro_metal_saurus">
      <span className="text-primary text-lg">{icon}</span>
      <span className="font-medium">{label}</span>
    </div>
    <div className="text-yankees_blue font-semibold text-right">{value}</div>
  </div>
);

export default Investigations;
